import { useCallback, useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import {
  applyCookieConsent,
  readCookieConsent,
  type CookieConsentChoice,
} from '../../lib/tracking/cookieConsent';
import * as S from './styles';

const EXIT_ANIMATION_MS = 500;

export type { CookieConsentChoice };

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return reduced;
}

export function CookieConsentBanner() {
  const reducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (readCookieConsent() !== null) return;
    setMounted(true);
    const id = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const dismiss = useCallback(
    (choice: CookieConsentChoice) => {
      applyCookieConsent(choice);

      if (reducedMotion) {
        setMounted(false);
        return;
      }

      setExiting(true);
      window.setTimeout(() => setMounted(false), EXIT_ANIMATION_MS);
    },
    [reducedMotion],
  );

  if (!mounted) return null;

  return (
    <S.Overlay
      $visible={visible}
      $exiting={exiting}
      role="dialog"
      aria-live="polite"
      aria-label="إشعار ملفات تعريف الارتباط"
    >
      <S.Banner>
        <S.Content>
          <S.TitleRow>
            <S.IconWrap aria-hidden>
              <Cookie size={20} strokeWidth={2} />
            </S.IconWrap>
            <S.Title>ملفات تعريف الارتباط</S.Title>
          </S.TitleRow>
          <S.Message>
            نستخدم الكوكيز لتحسين تجربتك وتحليل استخدام الموقع. بالمتابعة،
            أنت توافق على استخدامها.
          </S.Message>
        </S.Content>

        <S.Actions>
          <S.AcceptButton type="button" onClick={() => dismiss('accepted')}>
            قبول
          </S.AcceptButton>
          <S.RejectButton type="button" onClick={() => dismiss('rejected')}>
            رفض
          </S.RejectButton>
        </S.Actions>
      </S.Banner>
    </S.Overlay>
  );
}

export default CookieConsentBanner;
